import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SignIn } from "@clerk/clerk-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion, REGIONS } from "@/contexts/RegionContext";
import { Button } from "@/components/ui/button";
import { Loader2, Check, KeyRound } from "lucide-react";
import { Flag } from "@/components/Flag";

// Bundled, not fetched. This was a URL on Emergent's CDN carrying a `job_`
// identifier from the platform workspace, so it was tied to one platform job
// rather than a stable path — it could have stopped resolving before the platform
// was switched off, and the login page is where a missing logo shows first.
import logoUrl from "@/assets/simplyfunded-logo.jpg";

const SF_BLUE = "#3B70E0";


export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, clerkReady, logout } = useAuth();
  const { currentRegion, switchRegion, regionConfig } = useRegion();

  // Set when the backend rejected the session (api.js 401 handler). Breaks
  // the login↔app bounce loop: sign out once instead of navigating back in.
  const authFailed = useRef(sessionStorage.getItem("sf_auth_failed") === "1");
  const [showSessionNotice, setShowSessionNotice] = useState(authFailed.current);

  // Region selection step
  const [showRegionSelect, setShowRegionSelect] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(currentRegion);

  // Dev-access (password-gated bypass) — collapsed behind a small link
  const [showTeamAccess, setShowTeamAccess] = useState(false);
  const [devPass, setDevPass] = useState("");
  const [devBusy, setDevBusy] = useState(false);
  const [devErr, setDevErr] = useState("");

  const handleDevAccess = async () => {
    if (!devPass.trim() || devBusy) return;
    setDevBusy(true);
    setDevErr("");
    try {
      // `credentials: "include"` is what lets the backend's httpOnly session
      // cookie be set from a different origin (:3000 → :8001 locally). Without
      // it the response arrives, the Set-Cookie is discarded, and the login
      // appears to succeed while every following request is unauthenticated.
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dev-bypass`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: devPass }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      // The session token is NOT stored — it is in an httpOnly cookie the
      // browser sends on its own, so no script can read it. `sf_user` is kept
      // for what it is: display data (name and role in the header). It is not
      // a credential and the backend never reads it.
      localStorage.setItem("sf_user", JSON.stringify(data.user));
      window.location.href = "/";
    } catch (e) {
      setDevErr(e.message || "Login failed");
      setDevBusy(false);
    }
  };

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (!authFailed.current) return;
    sessionStorage.removeItem("sf_auth_failed");
    if (isAuthenticated && !loading) {
      authFailed.current = false;
      logout(); // one-time sign-out — the stale session was rejected by the backend
    }
  }, [isAuthenticated, loading, logout]);

  useEffect(() => {
    if (authFailed.current) return; // don't bounce back in with a rejected session
    if (isAuthenticated && !loading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Region Selection Screen ──────────────────────────────────────────
  if (showRegionSelect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-blue-50/40 p-4">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <img src={logoUrl} alt="SimplyFunded" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900">Select Region</h1>
            <p className="text-sm text-slate-500 mt-1">Choose the portal you want to access</p>
          </div>

          {showSessionNotice && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="session-expired-notice">
              Your session could not be verified, so you've been signed out — please sign in again.
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {Object.entries(REGIONS).map(([code, region]) => (
              <button
                key={code}
                onClick={() => !region.comingSoon && setSelectedRegion(code)}
                disabled={!!region.comingSoon}
                className={`
                  relative p-5 rounded-xl border-2 transition-all duration-200
                  flex flex-col items-center justify-center gap-2
                  ${region.comingSoon
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : 'hover:shadow-lg hover:scale-[1.02]'}
                  ${!region.comingSoon && selectedRegion === code
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : !region.comingSoon ? 'border-gray-200 bg-white hover:border-gray-300' : ''}
                `}
                data-testid={`region-select-${code.toLowerCase()}`}
              >
                {!region.comingSoon && selectedRegion === code && (
                  <div className="absolute top-2 right-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
                {region.comingSoon && (
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
                <Flag code={region.flagCode} size={44} />
                <span className={`font-semibold text-xs text-center ${
                  !region.comingSoon && selectedRegion === code ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  {region.name}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  !region.comingSoon && selectedRegion === code
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {region.currency}
                </span>
              </button>
            ))}
          </div>

          <Button
            onClick={() => { switchRegion(selectedRegion); setShowRegionSelect(false); }}
            className="w-full mt-6"
            style={{ backgroundColor: SF_BLUE }}
            data-testid="region-continue-btn"
          >
            Continue to {REGIONS[selectedRegion]?.name} Portal
          </Button>
        </div>
      </div>
    );
  }

  // ── Sign-In Screen ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-blue-50/40 p-4">
      <div className="w-full max-w-md flex flex-col items-center gap-5">
        {/* Brand header */}
        <div className="text-center">
          <img src={logoUrl} alt="SimplyFunded" className="h-16 w-auto mx-auto mb-3" />
          <button
            onClick={() => setShowRegionSelect(true)}
            className="inline-flex items-center gap-2 mx-auto px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 shadow-sm transition-colors text-sm"
            data-testid="change-region-btn"
          >
            <Flag code={regionConfig?.flagCode} size={18} />
            <span className="text-gray-700 font-medium">{regionConfig?.name} Portal</span>
            <span className="text-xs text-blue-600 hover:underline">Change</span>
          </button>
        </div>

        {/* Clerk sign-in — branded, footer/dev badge hidden */}
        {clerkReady ? (
          <div data-testid="clerk-signin">
            <SignIn
              routing="hash"
              forceRedirectUrl={from}
              appearance={{
                layout: { logoPlacement: "none" },
                elements: {
                  rootBox: "mx-auto",
                  card: "shadow-xl border border-slate-200 rounded-2xl",
                  logoBox: "hidden",
                  footer: "hidden",
                  socialButtons: "hidden",
                  socialButtonsBlockButton: "hidden",
                  button__google: "hidden",
                  dividerRow: "hidden",
                  headerTitle: "text-slate-900",
                  headerSubtitle: "text-slate-500",
                  formButtonPrimary: { backgroundColor: SF_BLUE },
                },
              }}
            />
          </div>
        ) : (
          <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-xl p-6 text-sm text-slate-600 text-center" data-testid="clerk-unavailable-note">
            Sign-in couldn't load on this domain right now.
            <br />Use <strong>Team access</strong> below.
          </div>
        )}

        <p className="text-xs text-center text-slate-400">
          You'll stay signed in on this device until you sign out.
          <br />Access is by invitation only — contact your admin if you need an account.
        </p>

        {/* Team access — tucked behind a subtle link */}
        <div className="w-full max-w-sm text-center">
          {!showTeamAccess ? (
            <button
              onClick={() => setShowTeamAccess(true)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="team-access-toggle"
            >
              <KeyRound className="w-3 h-3" /> Team access
            </button>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 text-left space-y-2">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-slate-500" /> Team access
              </div>
              <p className="text-[11px] text-slate-500">
                Use the shared access password if the sign-in above is unavailable.
              </p>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={devPass}
                  onChange={(e) => { setDevPass(e.target.value); setDevErr(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleDevAccess()}
                  placeholder="Access password"
                  className="flex-1 h-9 px-3 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="dev-bypass-password-input"
                />
                <Button
                  onClick={handleDevAccess}
                  disabled={devBusy || !devPass.trim()}
                  variant="outline"
                  data-testid="dev-bypass-btn"
                >
                  {devBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
                </Button>
              </div>
              {devErr && (
                <p className="text-xs text-red-600" data-testid="dev-bypass-error">{devErr}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
